<template>
  <div>
    <div
      v-if="showPreAuthorizationNumber"
      class="row"
    >
      <label class="col-sm-3 control-label pt-10">
        {{ getPartnerFieldLabel({ field: preAuthField, defaultValue: 'Pre-authorization #' }) }}
      </label>
      <div class="col-sm-5">
        <sanitized-input
          :id="preAuthField"
          :model-value="balance.preAuthorizationNumber"
          :placeholder="getPartnerFieldPlaceholder(preAuthField)"
          :required="isPartnerFieldRequired(preAuthField) || group.isPreAuthorizationNumberRequired"
          :validate="validatePreAuthorizationNumber"
          @update:model-value="emit('update-balance', { preAuthorizationNumber: $event ?? '' })"
        />
      </div>
    </div>
    <div class="row">
      <label class="col-sm-3 control-label pt-10">
        {{ getPartnerFieldLabel({ field: beginningDateField, defaultValue: 'Begin Date', required: true }) }}
      </label>
      <div class="col-sm-5">
        <date-picker
          :model-value="balance.beginningDate || null"
          @update:model-value="setDate('beginningDate', $event)"
        />
      </div>
    </div>
    <div class="row">
      <label class="col-sm-3 control-label pt-10">
        {{ getPartnerFieldLabel({ field: suspensionDateField, defaultValue: 'Suspension Date', required: true }) }}
      </label>
      <div class="col-sm-5">
        <date-picker
          :model-value="balance.suspensionDate || null"
          @update:model-value="setDate('suspensionDate', $event)"
        />
      </div>
    </div>
    <div
      v-if="isPartnerFieldVisible(amountField)"
      class="row"
    >
      <label class="col-sm-3 control-label pt-10">
        {{ getPartnerFieldLabel({ field: amountField, defaultValue: 'Amount' }) }}
      </label>
      <div class="col-sm-5">
        <el-input
          v-if="!isRides"
          v-model="amountInput"
          type="number"
          step="0.01"
          :min="0"
          :required="isPartnerFieldRequired(amountField)"
        >
          <template #prepend>
            $
          </template>
        </el-input>
        <number-input
          v-else
          v-model="amountInput"
          type="number"
          step="1"
          :min="0"
          :required="isPartnerFieldRequired(amountField)"
          class="form-control available-rides-input"
          :placeholder="getPartnerFieldPlaceholder(amountField) || 'Enter Amount'"
        />
      </div>
    </div>
    <div
      v-if="balance.id != null"
      class="row"
    >
      <div class="col-sm-5 offset-sm-3 mb-3">
        <button
          type="button"
          class="btn btn-outline-danger btn-sm"
          @click="emit('remove')"
        >
          Remove budget
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { usePartner } from '@/use/partner';
import util from '@/Utils/util';
import { isRidesAllowanceKind } from '../Partners/Subsidy/budgetBalanceForm';
import { formatBudgetDate, type EditableBudgetGroup, type EditableBudgetRow, type PartnerBudgetBalancePatch } from './partnerMemberBudget';

const props = defineProps<{
  group: EditableBudgetGroup;
  balance: EditableBudgetRow;
}>();

const emit = defineEmits<{
  'update-balance': [patch: PartnerBudgetBalancePatch];
  remove: [];
}>();

const { isPartnerFieldVisible, isPartnerFieldRequired, getPartnerFieldLabel, getPartnerFieldPlaceholder, validatePreAuthorizationNumber } = usePartner();

const isRides = computed(() => isRidesAllowanceKind(props.group.kind));
const beginningDateField = computed(() => (isRides.value ? 'budgetRidesBeginningDate' : 'budgetCreditsBeginningDate'));
const suspensionDateField = computed(() => (isRides.value ? 'budgetRidesSuspensionDate' : 'budgetCreditsSuspensionDate'));
const amountField = computed(() => (isRides.value ? 'budgetRidesAmount' : 'budgetCreditsAmount'));
const preAuthField = computed(() => (isRides.value ? 'budgetRidesPreAuthorizationNumber' : 'budgetCreditsPreAuthorizationNumber'));
const showPreAuthorizationNumber = computed(() => isPartnerFieldVisible(preAuthField.value));

const setDate = (field: 'beginningDate' | 'suspensionDate', value: unknown) => {
  emit('update-balance', { [field]: formatBudgetDate(value) });
};

const amountInput = ref<string | number | null>(props.balance.amount ?? '');

watch(amountInput, (value) => {
  const amount = util.getOptionalNumber(value);
  if (amount === props.balance.amount) {
    return;
  }
  emit('update-balance', { amount });
});

watch(
  () => props.balance,
  (balance) => {
    amountInput.value = balance.amount ?? '';
  },
);

watch(
  () => props.balance.amount,
  (amount) => {
    if (util.getOptionalNumber(amountInput.value) !== amount) {
      amountInput.value = amount ?? '';
    }
  },
);
</script>

<style lang="scss" scoped>
.available-rides-input {
  max-width: none !important;
  width: 100% !important;
  height: 40px !important;
  text-align: left !important;
}
</style>
